import { useVisibilityChangeEvent } from "@faire/deprecated-retailer-visitor-shared/hooks/useVisibilityChangeEvent";
import { getIsRobot } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getIsRobot";
import { trackBrandTileHeaderActionCategoryImpression } from "@faire/web-api--source/events/brandTile/actionCategoryImpression/header";
import { trackBrandTileTileActionCategoryImpression } from "@faire/web-api--source/events/brandTile/actionCategoryImpression/tile";
import React, { useCallback, useRef } from "react";

import { BrandImpressionType } from "../BrandImpressionType";

import {
  IntersectionObserverProvider,
  UnmountProvider,
} from "./BrandTileImpressionContexts";
import { ImpressionProviderProps } from "./ImpressionProviderProps";

export const BrandTileViewImpressionProvider: React.FC<
  React.PropsWithChildren<ImpressionProviderProps>
> = ({
  children,
  requestId,
  screenContext,
  containerContext,
  contextToken,
  type,
}) => {
  const dwellTimerMapRef = useRef<IDwellTimerMap>({});

  const handleUnmount = useCallback(
    (brandToken: string, impressionId: string) => {
      reportDwellTimer({
        requestId,
        impressionId,
        brandToken,
        screenContext,
        containerContext,
        contextToken,
        dwellTimerMapRef,
        type,
      });
    },
    [containerContext, requestId, screenContext, contextToken, type]
  );

  useVisibilityChangeEvent((visibilityState) => {
    if (visibilityState === "hidden") {
      const brandTokens = Object.keys(dwellTimerMapRef.current);
      brandTokens.forEach((brandToken) => {
        const impressionId =
          dwellTimerMapRef.current[brandToken]?.impressionId ?? "";
        reportDwellTimer({
          requestId,
          impressionId,
          brandToken,
          screenContext,
          containerContext,
          contextToken,
          dwellTimerMapRef,
          type,
        });
      });
    }
  });

  const handleIntersectionObserverCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const brandToken = entry.target.getAttribute("data-brand-token") ?? "";
        const impressionId =
          entry.target.getAttribute("data-brand-impression-id") ?? "";
        const dwellTimer = dwellTimerMapRef.current[brandToken];
        if (entry.isIntersecting) {
          if (dwellTimer) {
            if ((dwellTimer.maxThreshold ?? 0) < entry.intersectionRatio) {
              dwellTimerMapRef.current[brandToken]!.maxThreshold =
                entry.intersectionRatio;
            }
          } else {
            dwellTimerMapRef.current[brandToken] = {
              start: Date.now(),
              maxThreshold: entry.intersectionRatio,
              impressionId,
            };
          }
        } else if (dwellTimer) {
          reportDwellTimer({
            requestId,
            impressionId,
            brandToken,
            screenContext,
            containerContext,
            contextToken,
            dwellTimerMapRef,
            type,
          });
        }
      });
    },
    [containerContext, requestId, screenContext, contextToken, type]
  );

  if (getIsRobot()) {
    return children;
  }

  return (
    <UnmountProvider onUnmount={handleUnmount}>
      <IntersectionObserverProvider
        callback={handleIntersectionObserverCallback}
        options={{ threshold: TWO_PERCENT_THRESHOLD, rootMargin: ROOT_MARGIN }}
      >
        {children}
      </IntersectionObserverProvider>
    </UnmountProvider>
  );
};

const reportDwellTimer = (args: {
  requestId: string | undefined;
  impressionId: string;
  brandToken: string;
  screenContext: string;
  containerContext: string;
  contextToken: string | undefined;
  dwellTimerMapRef: React.RefObject<IDwellTimerMap>;
  type: BrandImpressionType | undefined;
}) => {
  const dwellTimer = args.dwellTimerMapRef.current[args.brandToken];
  if (dwellTimer) {
    const { start, maxThreshold } = dwellTimer;
    const stop = Date.now();
    switch (args.type) {
      case BrandImpressionType.HEADER:
        trackBrandTileHeaderActionCategoryImpression(
          args.requestId ?? "",
          args.impressionId,
          args.brandToken,
          args.screenContext,
          args.containerContext,
          args.contextToken ?? "",
          (stop - start).toString(),
          maxThreshold.toString()
        );
        break;
      case BrandImpressionType.TILE:
      default:
        trackBrandTileTileActionCategoryImpression(
          args.requestId ?? "",
          args.impressionId,
          args.brandToken,
          args.screenContext,
          args.containerContext,
          args.contextToken ?? "",
          (stop - start).toString(),
          maxThreshold.toString()
        );
    }

    delete args.dwellTimerMapRef.current[args.brandToken];
  }
};

type IDwellTimerMap = Record<
  string,
  { start: number; maxThreshold: number; impressionId: string }
>;

const ROOT_MARGIN = "0px 0px 0px 0px";
const TWO_PERCENT_THRESHOLD = new Array(51)
  .fill(0)
  .map((_, index) => index * 2)
  // this ensures no 0.15000000001 values
  .map((percent) => percent / 100);
